import CloseIcon from '@mui/icons-material/Close';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  List,
  useTheme,
} from '@mui/material';
import { ModalMessageInUserManagement, TypeModalDelete } from 'common/defines/clients';
import Scrollbars from 'react-custom-scrollbars';

type WarningDialogProps = {
  title: string;
  data: any;
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onSubmit: () => void;
  cancelLabel?: string;
  submitLabel: string;
  clientAt?: TypeModalDelete;
  clientName?: string;
  userManagementAction?: ModalMessageInUserManagement;
} & DialogProps;

const WarningDialog = ({
  title,
  data,
  open,
  onClose,
  onCancel,
  onSubmit,
  cancelLabel = 'Close',
  submitLabel,
  clientAt,
  clientName,
  userManagementAction,
  ...props
}: WarningDialogProps) => {
  const theme = useTheme();

  const styles = {
    modalStyles: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'center',
      bgColor: theme.palette.background.paper,
    },
    btnCloseModal: {
      position: 'absolute',
      top: 0,
      right: 0,
      color: '#9E9E9E',
      maxWidth: '60px',
      p: '10px 0',
    },
    iconWarningModal: {
      width: '50px',
      height: '50px',
      mt: '40px',
      borderRadius: '50%',
      alignSelf: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    titleModal: {
      fontFamily: 'Barlow',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '20px',
      color: theme.palette.mode === 'dark' ? '#fff' : '#5E5873',
      pb: '31px',
    },
    descriptionModal: {
      fontFamily: 'Barlow',
      fontStyle: 'normal',
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '18px',
      color: '#737373',
    },
  };

  return (
    <Dialog
      {...props}
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm">
      <Box
        sx={{
          ...styles.modalStyles,
          background: theme.palette.background.paper,
        }}>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            p: 0,
            position: 'absolute',
            right: 8,
            top: 8,
            '.MuiSvgIcon-root': {
              width: '16px',
              height: '16px',
            },
            color: (theme) => theme.palette.grey[500],
          }}>
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            ...styles.iconWarningModal,
            color: '#DA3E3E',
            backgroundColor: 'rgba(255, 70, 70, 0.15)',
          }}>
          <ReportProblemRoundedIcon
            sx={{
              alignSelf: 'center',
              fontSize: '26px',
            }}
          />
        </Box>
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            ...styles.titleModal,
            background: theme.palette.background.paper,
          }}>
          {title}
        </DialogTitle>
        <DialogContent
          sx={{
            background: theme.palette.background.paper,
            width: '100%',
          }}>
          <Box sx={{ height: 'fit-content' }}>
            <Scrollbars style={{ height: '300px' }} autoHide>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {data && Array.isArray(data) ? (
                  data.map((item, i) => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }} key={i}>
                      <div>{item?.error}</div>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                    <div>{data}</div>
                  </Box>
                )}
              </List>
            </Scrollbars>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            background: theme.palette.background.paper,
          }}>
          {onCancel && (
            <Button
              color="error"
              variant="contained"
              sx={{
                minWidth: '160px',
                height: '40px',
                borderRadius: '5px',
                p: '12px 10px',
                mb: '30px',
                textTransform: 'none',
                color: '#fff',
              }}
              onClick={onCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            sx={{
              minWidth: '160px',
              height: '40px',
              borderRadius: '5px',
              p: '12px 10px',
              mb: '30px',
              textTransform: 'none',
              color: '#fff',
            }}
            onClick={onSubmit}>
            {submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default WarningDialog;
